"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScreenFlowSyncResponse = exports.ScreenFlowSnapshot = exports.SaveScreenFlow = exports.CreateScreenFlow = exports.ScreenFlowAdmin = exports.ScreenFlowStatus = exports.JourneyGraph = exports.JourneyEdge = exports.JourneyNode = exports.ScreenManifest = exports.ScreenNode = exports.ScreenEvent = exports.ScreenPort = exports.ScreenSurface = void 0;
exports.validateJourneyGraph = validateJourneyGraph;
const zod_1 = require("zod");
/**
 * Screen-flow / journey designer contracts (RFL-142, ADR-0009).
 *
 * Two shapes live here:
 *  1. The **screen manifest** the terminal app publishes (RFL-143) — the
 *     node palette for the designer. BOS vendors the manifest JSON and
 *     validates it against `ScreenManifest` at boot; it does NOT
 *     hand-maintain the vocabulary.
 *  2. The **journey graph** BOS authors — instances of manifest screens
 *     (nodes) wired by the events each screen declares (edges). Persisted
 *     + published + synced to terminals like `terminal_flows`; the
 *     terminal flow interpreter runs it.
 *
 * Mirrors `reform-lite-terminals/.../screen_manifest.dart` +
 * `docs/contracts/SCREEN_MANIFEST.md`. A change here is a pin-bumping
 * wire-shape event.
 */
exports.ScreenSurface = zod_1.z.enum(['pos', 'sco', 'attendant']);
// ---------- Manifest (published by terminals) ----------
/** A typed param a screen consumes/produces. `type` is a domain type name. */
exports.ScreenPort = zod_1.z.object({
    name: zod_1.z.string().min(1),
    type: zod_1.z.string().min(1),
});
/** A transition source a screen can emit (becomes an outgoing edge). */
exports.ScreenEvent = zod_1.z.object({
    name: zod_1.z.string().min(1),
    label: zod_1.z.string().optional(),
});
exports.ScreenNode = zod_1.z.object({
    id: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    isStart: zod_1.z.boolean(),
    isTerminal: zod_1.z.boolean(),
    insertable: zod_1.z.boolean(),
    events: zod_1.z.array(exports.ScreenEvent),
    inputs: zod_1.z.array(exports.ScreenPort),
    outputs: zod_1.z.array(exports.ScreenPort),
});
exports.ScreenManifest = zod_1.z.object({
    manifestVersion: zod_1.z.string().min(1),
    surface: exports.ScreenSurface,
    screens: zod_1.z.array(exports.ScreenNode),
});
// ---------- Journey graph (authored by BOS, run by terminals) ----------
/** An instance of a manifest screen placed on the canvas. */
exports.JourneyNode = zod_1.z.object({
    /** Instance id (a screen may appear more than once in a journey). */
    nodeId: zod_1.z.string().min(1).max(64),
    /** References a `ScreenNode.id` in the surface's manifest. */
    screenId: zod_1.z.string().min(1),
    position: zod_1.z.object({ x: zod_1.z.number(), y: zod_1.z.number() }),
});
/** "When `source` emits `sourceEvent`, go to `target`." */
exports.JourneyEdge = zod_1.z.object({
    id: zod_1.z.string().min(1).max(64),
    source: zod_1.z.string().min(1),
    sourceEvent: zod_1.z.string().min(1),
    target: zod_1.z.string().min(1),
});
exports.JourneyGraph = zod_1.z.object({
    /** The manifest version this graph was authored against (skew checks). */
    manifestVersion: zod_1.z.string().min(1),
    nodes: zod_1.z.array(exports.JourneyNode).max(200),
    edges: zod_1.z.array(exports.JourneyEdge).max(500),
});
exports.ScreenFlowStatus = zod_1.z.enum(['draft', 'published']);
// ---------- Admin REST + sync shapes ----------
exports.ScreenFlowAdmin = zod_1.z.object({
    id: zod_1.z.string(),
    surface: exports.ScreenSurface,
    locationId: zod_1.z.string().nullable(),
    name: zod_1.z.string().min(1).max(200),
    status: exports.ScreenFlowStatus,
    version: zod_1.z.number().int(),
    manifestVersion: zod_1.z.string(),
    graph: exports.JourneyGraph,
    publishedAt: zod_1.z.string().datetime().nullable(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.CreateScreenFlow = zod_1.z.object({
    surface: exports.ScreenSurface,
    name: zod_1.z.string().min(1).max(200),
    locationId: zod_1.z.string().nullable().default(null),
});
exports.SaveScreenFlow = zod_1.z.object({
    expectedVersion: zod_1.z.number().int().min(0),
    name: zod_1.z.string().min(1).max(200).optional(),
    graph: exports.JourneyGraph,
});
exports.ScreenFlowSnapshot = zod_1.z.object({
    id: zod_1.z.string(),
    surface: exports.ScreenSurface,
    locationId: zod_1.z.string().nullable(),
    name: zod_1.z.string(),
    version: zod_1.z.number().int(),
    manifestVersion: zod_1.z.string(),
    graph: exports.JourneyGraph,
});
exports.ScreenFlowSyncResponse = zod_1.z.object({
    flows: zod_1.z.array(exports.ScreenFlowSnapshot),
    serverTime: zod_1.z.string().datetime(),
});
/**
 * Structural validation of a journey graph against its surface manifest.
 * Pure (no I/O) so the backend (publish gate), tests, and the admin UI
 * can all share it. Returns [] when the graph is valid.
 *
 * Rules: every node references a known screen; node ids unique; each edge
 * connects known nodes and its `sourceEvent` is declared by the source
 * screen; terminal screens have no outgoing edges; exactly one entry node
 * (no incoming edges, and its screen is a start screen); every node is
 * reachable from the entry. (Port-type compatibility is a v2 concern.)
 */
function validateJourneyGraph(graph, manifest) {
    const issues = [];
    const screenById = new Map(manifest.screens.map((s) => [s.id, s]));
    if (graph.manifestVersion !== manifest.manifestVersion) {
        issues.push({
            code: 'MANIFEST_VERSION_MISMATCH',
            message: `graph targets manifest ${graph.manifestVersion} but the current manifest is ${manifest.manifestVersion}`,
        });
    }
    const nodeById = new Map();
    for (const n of graph.nodes) {
        if (nodeById.has(n.nodeId)) {
            issues.push({ code: 'DUPLICATE_NODE', message: `duplicate node id ${n.nodeId}` });
        }
        nodeById.set(n.nodeId, n);
        if (!screenById.has(n.screenId)) {
            issues.push({
                code: 'UNKNOWN_SCREEN',
                message: `node ${n.nodeId} references unknown screen ${n.screenId}`,
            });
        }
    }
    const incoming = new Map();
    for (const n of graph.nodes)
        incoming.set(n.nodeId, 0);
    const adjacency = new Map();
    for (const e of graph.edges) {
        const src = nodeById.get(e.source);
        const tgt = nodeById.get(e.target);
        if (src === undefined || tgt === undefined) {
            issues.push({
                code: 'DANGLING_EDGE',
                message: `edge ${e.id} references a missing node`,
            });
            continue;
        }
        const srcScreen = screenById.get(src.screenId);
        if (srcScreen !== undefined) {
            if (srcScreen.isTerminal) {
                issues.push({
                    code: 'TERMINAL_HAS_OUTGOING',
                    message: `terminal screen ${src.screenId} (node ${src.nodeId}) cannot have outgoing edges`,
                });
            }
            if (!srcScreen.events.some((ev) => ev.name === e.sourceEvent)) {
                issues.push({
                    code: 'UNKNOWN_EVENT',
                    message: `edge ${e.id}: screen ${src.screenId} does not emit event "${e.sourceEvent}"`,
                });
            }
        }
        incoming.set(e.target, (incoming.get(e.target) ?? 0) + 1);
        const list = adjacency.get(e.source) ?? [];
        list.push(e.target);
        adjacency.set(e.source, list);
    }
    if (graph.nodes.length > 0) {
        const entries = graph.nodes.filter((n) => (incoming.get(n.nodeId) ?? 0) === 0);
        if (entries.length === 0) {
            issues.push({ code: 'NO_ENTRY', message: 'no entry node (every node has an incoming edge)' });
        }
        else if (entries.length > 1) {
            issues.push({
                code: 'MULTIPLE_ENTRIES',
                message: `multiple entry nodes: ${entries.map((n) => n.nodeId).join(', ')}`,
            });
        }
        else {
            const entry = entries[0];
            const entryScreen = screenById.get(entry.screenId);
            if (entryScreen !== undefined && !entryScreen.isStart) {
                issues.push({
                    code: 'ENTRY_NOT_START',
                    message: `entry node ${entry.nodeId} uses non-start screen ${entry.screenId}`,
                });
            }
            // Reachability from the single entry.
            const seen = new Set([entry.nodeId]);
            const stack = [entry.nodeId];
            while (stack.length > 0) {
                const cur = stack.pop();
                for (const next of adjacency.get(cur) ?? []) {
                    if (!seen.has(next)) {
                        seen.add(next);
                        stack.push(next);
                    }
                }
            }
            for (const n of graph.nodes) {
                if (!seen.has(n.nodeId)) {
                    issues.push({
                        code: 'UNREACHABLE',
                        message: `node ${n.nodeId} is not reachable from the entry`,
                    });
                }
            }
        }
    }
    return issues;
}
//# sourceMappingURL=screen-flow.js.map